import React from 'react';
import { motion } from 'motion/react';
import {
  Smartphone,
  Code,
  Palette,
  Rocket,
  Shield,
  Zap,
  Users,
  HeadphonesIcon,
  CheckCircle,
  ArrowRight,
  Apple,
  Layers,
  Database,
  Cloud,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SpamProtectedForm from '../../components/SpamProtectedForm';
import '../../styles/services.css';

const features = [
  { icon: Smartphone, title: 'Native iOS & Android', desc: 'Build high-performance native apps for both platforms' },
  { icon: Layers, title: 'Cross-Platform Development', desc: 'React Native & Flutter for cost-effective multi-platform apps' },
  { icon: Palette, title: 'UI/UX Design', desc: 'Beautiful, intuitive interfaces that users love' },
  { icon: Database, title: 'Backend Integration', desc: 'Robust APIs and database architecture' },
  { icon: Cloud, title: 'Cloud Deployment', desc: 'Scalable cloud infrastructure for your apps' },
  { icon: Shield, title: 'Security First', desc: 'Enterprise-grade security and data protection' },
];

const process = [
  { step: '01', title: 'Discovery', desc: 'Understanding your vision, goals, and requirements' },
  { step: '02', title: 'Design', desc: 'Creating wireframes and stunning UI/UX designs' },
  { step: '03', title: 'Development', desc: 'Building your app with clean, scalable code' },
  { step: '04', title: 'Testing', desc: 'Rigorous QA to ensure flawless performance' },
  { step: '05', title: 'Launch', desc: 'Deploying to app stores with full support' },
  { step: '06', title: 'Support', desc: 'Ongoing maintenance and updates' },
];

const AppDevelopmentPage = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="service-hero app-dev">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="service-hero-content"
          >
            <span className="service-badge">
              <Smartphone size={16} /> Mobile & App Development
            </span>
            <h1>Transform Your Ideas Into Powerful Apps</h1>
            <p>We build beautiful, high-performance mobile applications that drive business growth and deliver exceptional user experiences.</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn-service-primary">
                Start Your Project <ArrowRight size={18} />
              </a>
              <a href="#features" className="btn-service-secondary">
                Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="service-features" id="features">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">What We Offer</span>
            <h2>Full-Stack App Development Services</h2>
            <p>From concept to launch, we handle every aspect of your mobile app development</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="service-process">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Our Process</span>
            <h2>How We Build Your App</h2>
            <p>A proven methodology that delivers results on time and on budget</p>
          </div>
          
          <div className="process-grid">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="process-card"
              >
                <span className="process-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="service-contact" id="contact">
        <div className="container-custom">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Let's Build Something Amazing</h2>
              <p>Ready to turn your app idea into reality? Get in touch and let's discuss your project.</p>
              <ul className="contact-benefits">
                <li><CheckCircle size={20} /> Free project consultation</li>
                <li><CheckCircle size={20} /> Detailed cost estimate</li>
                <li><CheckCircle size={20} /> Timeline and milestone planning</li>
                <li><CheckCircle size={20} /> Technical recommendations</li>
              </ul>
            </div>
            
            <SpamProtectedForm 
              emailSubject="App Development Inquiry"
              submitText="Send Message"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AppDevelopmentPage;
